// ─── Re-exports ────────────────────────────────────────────────────────────────
// New `contacts/` domain types (Phase 1 of CONTACTS_IMPLEMENTATION_PLAN).
// Existing `users/` types below are untouched per continuity guarantee §4a.
export type {
  Contact,
  ContactStatus,
  ContactSource,
  DeliveryPreference,
} from './contact';
export {
  CONTACT_STATUS_LABELS,
  DELIVERY_PREFERENCE_LABELS,
} from './contact';

// ─── Per-app role types ────────────────────────────────────────────────────────

export type OKRRole    = 'superadmin' | 'admin' | 'editor' | 'viewer';
export type EPRRole    = 'superadmin' | 'admin' | 'hr_admin' | 'leader' | 'employee';
export type PortalRole = 'superadmin' | 'admin' | 'viewer';

export interface AppRoles {
  okr?:    OKRRole;
  epr?:    EPRRole;
  portal?: PortalRole;
}

// ─── Grade config ──────────────────────────────────────────────────────────────

export type GradeId = 'G1' | 'G2' | 'G3' | 'G4' | 'G5' | 'G6' | 'G7';

export const GRADE_LABELS: Record<GradeId, string> = {
  G1: 'G1 — Entry',
  G2: 'G2 — Junior',
  G3: 'G3 — Mid',
  G4: 'G4 — Senior',
  G5: 'G5 — Specialist',
  G6: 'G6 — Manager / Lead',
  G7: 'G7 — Director+',
};

// ─── Company domains (enforce MS SSO) ─────────────────────────────────────────

export const COMPANY_DOMAINS = ['rascalrepublic.com', 'samaralombok.com', 'rinjanibay.com'];

export function isCompanyDomain(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase() ?? '';
  return COMPANY_DOMAINS.includes(domain);
}

// ─── User type (identity classification) ──────────────────────────────────────

export type UserType = 'employee' | 'external_worker' | 'vendor' | 'guest';

export const USER_TYPE_LABELS: Record<UserType, string> = {
  employee:        'Employee',
  external_worker: 'External Worker',
  vendor:          'Vendor',
  guest:           'Guest',
};

// ─── Auth type (login method) ─────────────────────────────────────────────────

export type AuthType = 'microsoft_sso' | 'external_identity' | 'local_invited';

export const AUTH_TYPE_LABELS: Record<AuthType, string> = {
  microsoft_sso:     'Microsoft SSO',
  external_identity: 'External Identity',
  local_invited:     'Local (Invited)',
};

// ─── Access policy (global override) ──────────────────────────────────────────

export type AccessPolicy = 'full_access' | 'limited_access' | 'no_access';

export const ACCESS_POLICY_LABELS: Record<AccessPolicy, string> = {
  full_access:    'Full Access',
  limited_access: 'Limited Access',
  no_access:      'No Access',
};

export const ACCESS_POLICY_COLORS: Record<AccessPolicy, string> = {
  full_access:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  limited_access: 'bg-amber-50 text-amber-700 border-amber-200',
  no_access:      'bg-red-50 text-red-700 border-red-200',
};

// ─── Employment type ──────────────────────────────────────────────────────────

export type EmploymentType = 'full_time' | 'part_time' | 'contract' | 'temporary';

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  full_time:  'Full Time',
  part_time:  'Part Time',
  contract:   'Contract',
  temporary:  'Temporary',
};

// ─── Account status (expanded) ────────────────────────────────────────────────

export type AccountStatus = 'invited' | 'active' | 'suspended' | 'disabled' | 'archived';

export const ACCOUNT_STATUS_LABELS: Record<AccountStatus, string> = {
  invited:   'Invited',
  active:    'Active',
  suspended: 'Suspended',
  disabled:  'Disabled',
  archived:  'Archived',
};

// ─── Tier classification ──────────────────────────────────────────────────────

export type AccessTier = 1 | 2 | 3;

export function getUserTier(authType?: AuthType, userType?: UserType): AccessTier {
  if (authType === 'microsoft_sso') return 1;
  if (userType === 'vendor' || userType === 'guest') return 3;
  return 2;
}

export const TIER_LABELS: Record<AccessTier, string> = {
  1: 'Tier 1 — Corporate SSO',
  2: 'Tier 2 — Operational Staff',
  3: 'Tier 3 — Vendor / Guest',
};

// ─── Unified Account User ──────────────────────────────────────────────────────

export interface AccountUser {
  id: string;
  uid: string;

  // Core identity
  name: string;
  displayName?: string;
  email: string;
  photoURL?: string;
  avatar?: string;

  // Identity classification
  user_type?: UserType;
  auth_type?: AuthType;
  access_policy?: AccessPolicy;

  // HRIS fields
  jobTitle?: string;
  grade?: GradeId | string;
  department?: string;
  phone?: string;
  startDate?: string;          // ISO date string
  bio?: string;
  nationality?: string;
  emergencyContact?: string;
  employee_id?: string;
  site?: string;
  employment_type?: EmploymentType;

  // Org
  unitId?: string;
  unitName?: string;
  directManagerId?: string;
  managerName?: string;

  // Access control
  role: string;                // legacy OKR role (kept for backward compat)
  appRoles?: AppRoles;
  appAccess?: string[];
  /**
   * Per-app login block. `true` = explicitly disabled (user signs out
   * immediately if they try to log in to that app); absent / `false` =
   * allowed. See docs/ACCESS_MODEL.md §"Per-app access control".
   */
  appDisabled?: {
    okr?:    boolean;
    epr?:    boolean;
    portal?: boolean;
  };
  isActive?: boolean;
  isLeader?: boolean;
  status?: AccountStatus;

  // Invitation tracking
  invitedAt?: any;             // Firestore Timestamp
  invitedBy?: string;
  lastLoginAt?: any;
}

// ─── Invite payload ───────────────────────────────────────────────────────────

/** EPR employee level — drives HR framework (hr_v1) access + per-level
 *  scoring weights. Derived from grade by the inviteUser Cloud Function
 *  unless explicitly supplied: G7→dept_head, G6→supervisor, else→general. */
export type EmployeeLevel = 'general' | 'supervisor' | 'dept_head';

export interface InvitePayload {
  name: string;
  email: string;
  jobTitle?: string;
  grade?: GradeId | string;
  department?: string;
  appRoles?: AppRoles;
  user_type?: UserType;
  /** OKR Tracker leader flag — gates dept-lead features + recap auto-include. */
  isLeader?: boolean;
  /** FK to /units doc — required for KR/dept lead resolution + EPR panel suggestion. */
  unitId?: string;
  /** Cached unit name — convenience copy to avoid extra lookup on display. */
  unitName?: string;
  /** FK to /users doc — line manager chain for EPR magic-link panel auto-suggest. */
  directManagerId?: string;
  /** EPR HR framework level — see EmployeeLevel above. */
  employeeLevel?: EmployeeLevel;
}

// ─── Import result ────────────────────────────────────────────────────────────

export interface ImportUserRow {
  name: string;
  email: string;
  jobTitle?: string;
  grade?: string;
  department?: string;
  user_type?: string;
  okrRole?: string;
  eprRole?: string;
  portalRole?: string;
  _valid?: boolean;
  _error?: string;
  _authType?: AuthType;
}

export interface ImportResult {
  created: number;
  skipped: number;
  errors: number;
  results: Array<{ email: string; uid?: string; status: 'created' | 'skipped' | 'error'; reason?: string }>;
}

// ─── Auth context ──────────────────────────────────────────────────────────────

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: string;
}
